import React, { useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { t } from "i18next";
import { Alert, Button, Card, Checkbox, Divider, Empty, Form, Grid, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import DatePicker from "../../core/components/DatePicker";
import {
    SaveOutlined,
    DeleteOutlined,
    EditOutlined,
    CreditCardOutlined,
    SwapOutlined,
    GlobalOutlined,
    WalletOutlined,
    HistoryOutlined,
    ImportOutlined
} from "@ant-design/icons";
import moment from "moment";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import Helper from "../../core/Helper";
import CashRegisterSelectList from "../cash-register/CashRegisterSelectList";
import CurrencyService from "../../core/service/CurrencyService";
import CurrencySelect from "../../core/components/CurrencySelect";
import AppConfig from "../../core/service/AppConfig";
import { useModal } from "../../core/context/ModalContext";
import PosTransfer from "./PosTransfer";
import PosCancel from "./PosCancel";
import PriceSummaryPreview from "../appointment/preview/PriceSummaryPreview";
import { Branding } from "../../core/service/BrandingService";

const { Text } = Typography;

export const Currency = {
    'TRY': '₺',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
}

export default function MultiplePaymentApp({ idList, onSave, onDelete, onCancel, patientId }) {
    const [operations, setOperations] = useState([]);
    const [otherOperations, setOtherOperations] = useState([]);
    const methodsRef = useRef([])
    const registersRef = useRef([])
    const [methods, setMethods] = useState([])
    const { hasPermission, formatPrice } = useUser();
    const formRef = useRef();
    const [paymentForm] = Form.useForm();
    const isDiff = Form.useWatch('diffPayment', paymentForm);
    const editingId = Form.useWatch('id', paymentForm);
    const editingAppointmentId = Form.useWatch('appointmentId', paymentForm);
    const [payments, setPayments] = useState([]);
    const [appointmentIdList, setAppointmentIdList] = useState(idList || []);
    const [currency, setCurrency] = useState('TRY')
    const [appointmentCurrency, setAppointmentCurrency] = useState('TRY')
    const [currencyValues, setCurrencyValues] = useState([])
    const [isEdit, setIsEdit] = useState(true);
    // Düzenlenen ödeme PAVO POS'a gönderilmiş mi? (tutar alanları kilitlenir)
    const [posLocked, setPosLocked] = useState(false);
    // Form her sıfırlandığında tutarı kalan borçla yeniden doldurmak için tetikleyici
    const [formResetKey, setFormResetKey] = useState(0);
    const { openModal, closeModal } = useModal()
    const { user } = useUser()
    const wrapperRef = useRef(null);

    // Modal gövdesinin beyaz padding'ini kaldır: gri zemin üstünde kartlar ayrışsın
    // (AppointmentPreviewApp ile aynı yöntem; yalnızca bu modalı etkiler)
    useEffect(() => {
        const modalBody = wrapperRef.current?.closest('.ant-modal-body');
        if (!modalBody) return;
        const originalPadding = modalBody.style.padding;
        modalBody.style.padding = '0';
        return () => { modalBody.style.padding = originalPadding; };
    }, []);

    // PAVO POS'a başarıyla gönderilmiş ve henüz iptal edilmemiş ödeme
    const isPosCompleted = (row) => !!row?.posTransactionId && !row?.posError

    const getPayments = async () => {
        if (appointmentIdList) {
            const res = await ApiService.Payment.GetPaymentsByAppointmentIds(appointmentIdList, true);
            if (res.success) setPayments(res.data)
        }
    }

    const deletePayment = async (paymentId, appointmentId) => {
        const res = await ApiService.Payment.DeletePayment(appointmentId, paymentId);
        if (res.success) {
            await getPayments();
            formRef.current.resetFields()
            setFormResetKey(k => k + 1)
            setPosLocked(false)
            onDelete && onDelete()
        }
    }

    const renderActionRow = (row) => {
        // Silinmiş ödeme salt okunur; düzenleme/silme/POS işlemi yapılamaz
        if (row?.isDeleted) return null
        const posCompleted = isPosCompleted(row)
        return (
        <Space size={2}>
            {Branding.feature('posEnable') && (
                posCompleted ? (
                    <Tooltip title={t('POS Cancel')}>
                        <Button size="small" type="text" icon={<CreditCardOutlined />} danger onClick={() => openModal('POS Cancel', <PosCancel appointmentId={row?.appointmentId} patientId={patientId} payment={row} onSuccess={async () => { closeModal(); await getPayments() }} />, () => { })} />
                    </Tooltip>
                ) : (
                    <Tooltip title={t('POS Transfer')}>
                        <Button size="small" icon={<SwapOutlined />} onClick={() => openModal('POS Transfer', <PosTransfer appointmentId={row?.appointmentId} patientId={patientId} payment={row} onSuccess={async () => { closeModal(); await getPayments() }} />, () => { })} />
                    </Tooltip>
                )
            )}
            {(!Helper.Date.isBeforeToday(row.paymentDate) || hasPermission(PERMISSIONS.EDIT_PAST_PAYMENT)) && hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                <Tooltip title={posCompleted ? t('This payment was sent to the POS, the amount cannot be changed. Cancel it on the POS first.') : t('Edit')}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => {
                        setIsEdit(false)
                        setPosLocked(posCompleted)
                        let payment = { ...row }
                        payment.paymentDate = moment(new Date(payment.paymentDate))
                        setCurrency(payment?.currency)
                        formRef.current.setFieldsValue({ ...payment })
                    }} />
                </Tooltip>
            )}
            {(!Helper.Date.isBeforeToday(row.paymentDate) || hasPermission(PERMISSIONS.EDIT_PAST_PAYMENT)) && hasPermission(PERMISSIONS.PAYMENT_DELETE) && (
                posCompleted ? (
                    <Tooltip title={t('This payment was sent to the POS, it cannot be deleted before the POS cancellation.')}>
                        <span style={{ cursor: 'not-allowed' }}>
                            <Button size="small" type="text" danger disabled icon={<DeleteOutlined />} style={{ pointerEvents: 'none' }} />
                        </span>
                    </Tooltip>
                ) : (
                    <Popconfirm title={t('Delete?')} onConfirm={() => deletePayment(row.id, row.appointmentId)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                )
            )}
        </Space>
        )
    }

    const getAppointments = async () => {
        if (appointmentIdList.length > 0) {
            let _operations = [], _otherOperations = []
            for (const id of appointmentIdList) {
                const res = await ApiService.Appointment.GetAppointment(id);
                setAppointmentCurrency(res.data.currency); setCurrency(res.data.currency);
                formRef.current.setFieldsValue({ currency: res.data.currency })
                if (res.success) {
                    _operations = [..._operations, ...res.data.appointmentOperations.map(x => ({ ...x, appointmentId: id, startDate: res.data.startDate, currency: res.data.currency }))]
                    _otherOperations = [..._otherOperations, ...res.data.appointmentOtherOperations.map(x => ({ ...x, appointmentId: id, startDate: res.data.startDate, currency: res.data.currency }))]
                }
            }
            setOperations(_operations);
            setOtherOperations(_otherOperations)
        } else {
            setOperations([]); setOtherOperations([])
        }
    }

    const loadPaymentMethods = async (firmId) => {
        if (methodsRef.current.length === 0) {
            const res = await ApiService.PaymentMethod.GetPaymentMethodList();
            if (res.success) methodsRef.current = res.data
        }
        if (firmId) {
            setMethods(methodsRef.current.filter(x => x?.firm?.id === firmId))
            formRef.current.setFieldsValue({ paymentMethodId: null })
        }
    }

    const onFinish = async (formData) => {
        setIsEdit(true); setCurrency(appointmentCurrency); setPosLocked(false)
        if (formData.diffPayment) {
            formData.amount = Math.abs(formData.amount) * -1
            formData.currencyAmount = Math.abs(formData.currencyAmount ?? 0) * -1
            formData.description = `${formData.description ? formData.description + ' - ' : ''}Diff.`
        }
        if (formData.id) {
            const res = await ApiService.Payment.EditPayment(formData.appointmentId, formData.id, { ...formData })
            if (res.success) { await getPayments(); formRef.current.resetFields(); setFormResetKey(k => k + 1) }
        } else {
            const res = await ApiService.Payment.AddPayments(appointmentIdList, { ...formData })
            if (res.success) { await getPayments(); formRef.current.resetFields(); setFormResetKey(k => k + 1) }
        }
        onSave && onSave()
    }

    useEffect(() => { getAppointments(); getPayments() }, [appointmentIdList])
    useEffect(() => { setAppointmentIdList(idList) }, [idList]);
    useEffect(() => {
        const load = async () => {
            const res = await CurrencyService.getPriceTRY(['USD', 'EUR', 'GBP'])
            setCurrencyValues(res)
        }
        load()
    }, []);

    // --- STYLES ---
    const screens = Grid.useBreakpoint();
    const isMobileLayout = !screens.md;
    // Geniş ekranda Ödeme Geçmişi sağ sütunda ayrı kart (İşlemler + Ödemeler solda, alt alta)
    const sideHistory = !!screens.xl;

    // --- TOPLAMLAR ---
    // İşlemler faturanın kalemleri gibi: toplam fiyat = kalemlerin toplamı (-1 = fiyatsız, sayılmaz)
    const allItems = [
        ...operations.map(x => ({ ...x, type: 1 })),
        ...otherOperations.map(x => ({ ...x, type: 2 })),
    ];
    const totalPrice = allItems.reduce((sum, x) => sum + (x?.price === -1 ? 0 : (x?.price || 0)), 0);
    const totalPaid = payments.filter(x => !x.isDeleted).reduce((sum, p) => sum + (p.currencyAmount || 0), 0);
    const remaining = totalPrice - totalPaid;
    const isMultiAppointment = (appointmentIdList?.length || 0) > 1;

    // Yeni ödeme formunda tutar kalan borçla dolu gelsin (yalnızca randevu para biriminde)
    const fillRemaining = () => {
        if (!formRef.current) return;
        formRef.current.setFieldsValue({ amount: remaining > 0 ? Number(remaining.toFixed(2)) : 0 });
    };

    useEffect(() => {
        if (isEdit && currency === appointmentCurrency && formRef.current && !formRef.current.isFieldTouched('amount')) {
            fillRemaining();
        }
    }, [remaining, isEdit, formResetKey]);

    const resetToNewPayment = () => {
        formRef.current.resetFields();
        formRef.current.setFieldsValue({ paymentDate: moment(), currency: appointmentCurrency });
        setCurrency(appointmentCurrency);
        setIsEdit(true);
        setPosLocked(false);
        fillRemaining();
    };

    const itemName = (row) => row.type === 1 ? row.operation?.name : row.name;
    const renderAmount = (row) => {
        const negative = (row.currencyAmount || 0) < 0;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 0, paddingRight: CELL_INSET }}>
                <Text delete={row?.isDeleted} type={row?.isDeleted ? 'secondary' : undefined}
                      style={{ whiteSpace: 'nowrap', fontWeight: 600, color: row?.isDeleted ? undefined : negative ? '#cf1322' : '#595959' }}>
                    {formatPrice(row.currencyAmount, { currency: appointmentCurrency })}
                </Text>
                {row.currency && row.currency !== appointmentCurrency && (
                    <Text type="secondary" delete={row?.isDeleted} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {formatPrice(row.amount, { currency: row.currency })}
                    </Text>
                )}
            </div>
        );
    };

    // Bölüm başlığı: nötr — renk yalnızca anlam taşıyan yerde (kalan borç, fark ödemesi, düzenleme, silme)
    const sectionTitle = (icon, text) => (
        <Space size={6}>
            <span style={{ color: '#8c8c8c', display: 'inline-flex' }}>{icon}</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{text}</span>
        </Space>
    );
    const sectionCard = { borderRadius: 8 };
    const sectionHead = { background: '#fafafa' };

    // --- ÖDEME DEFTERİ (giriş satırı + geçmiş) ---
    const canCreatePayment = hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT);
    // Ortak sütun şablonu: [Rnd.] Tarih | Kasa | Yöntem | Açıklama | Tutar | Aksiyon
    // Esnek (fr + alt sınır): sabit px toplamı modal genişliğini aşıp Kaydet/aksiyonları kart dışına itiyordu.
    const ledgerColumns = [
        isMultiAppointment ? '52px' : null,
        'minmax(110px, 1fr)',   // Tarih
        'minmax(100px, 1fr)',   // Kasa
        'minmax(100px, 1fr)',   // Yöntem
        'minmax(100px, 1.4fr)', // Açıklama
        'minmax(210px, 1.3fr)', // Tutar (döviz + ± + tutar)
        '96px',                 // Kaydet / aksiyonlar
    ].filter(Boolean).join(' ');
    const LEDGER_MIN_WIDTH = (isMultiAppointment ? 52 + 8 : 0) + 110 + 100 + 100 + 100 + 210 + 96 + 5 * 8 + 24;
    const ledgerRow = (extra = {}) => ({
        display: 'grid', gridTemplateColumns: ledgerColumns, gap: 8, alignItems: 'center', padding: '6px 12px', ...extra
    });
    const noMargin = { marginBottom: 0 };
    // Küçük boy Input/Select/DatePicker metni kenardan 8px içeride (7px padding + 1px çerçeve);
    // başlık ve geçmiş hücreleri aynı içerlemeyle başlar ki metinler giriş alanlarındaki metinle hizalansın
    const CELL_INSET = 8;
    const cell = { paddingLeft: CELL_INSET };
    const AMOUNT_CURRENCY_WIDTH = 84;

    // Giriş alanları — her biri tek kez render edilir (masaüstünde grid hücresi, mobilde alt alta form)
    const dateField = (
        <Form.Item name={'paymentDate'} style={noMargin} label={isMobileLayout ? t('Date') : undefined}>
            <DatePicker showTime format={'DD/MM HH:mm'} style={{ width: '100%' }} disabled={!hasPermission(PERMISSIONS.PAYMENT_EDIT_DATE)} locale={Helper.Date.getLocale()} />
        </Form.Item>
    );
    const registerField = (
        <Form.Item name={'cashRegisterId'} rules={[{ required: true, message: '' }]} style={noMargin} label={isMobileLayout ? t('Register') : undefined}>
            <CashRegisterSelectList currency={currency} style={{ width: '100%' }} placeholder={t('Register')} onList={(registers) => {
                registersRef.current = registers
                let reg = user?.userCashRegisters?.find(x => x.isDefault) ?? (user?.userCashRegisters?.length > 0 && user?.userCashRegisters[0])
                const exist = registers.find(x => x.id === reg?.cashRegisterId)
                formRef.current.setFieldsValue({ cashRegisterId: exist?.id })
                loadPaymentMethods(exist?.firmId)
            }} all={false} />
        </Form.Item>
    );
    const methodField = (
        <Form.Item name={'paymentMethodId'} rules={[{ required: true, message: '' }]} style={noMargin} label={isMobileLayout ? t('Method') : undefined}>
            <Select options={methods} fieldNames={{ label: 'name', value: 'id' }} style={{ width: '100%' }} placeholder={t('Method')} />
        </Form.Item>
    );
    const descriptionField = (
        <Form.Item name={'description'} style={noMargin} label={isMobileLayout ? t('Description') : undefined}>
            <Input placeholder={t('Description')} />
        </Form.Item>
    );
    // Tutar iki parça: üst (döviz + tutar) giriş satırının 1. satırında, alt (karşılık + fark ödemesi)
    // 2. satırda — böylece 2. satırın solundaki boşluğa Kaydet yerleşebiliyor
    // Kalanı doldur: tutar kutusunun içinde (suffix) küçük ikon; kalan tutar tooltip'te
    const fillRemainingIcon = isEdit && currency === appointmentCurrency && remaining > 0 && !posLocked ? (
        <Tooltip title={`${t('Fill Remaining')}: ${formatPrice(remaining, { currency: appointmentCurrency })}`}>
            <ImportOutlined onClick={fillRemaining} style={{ color: '#1677ff', cursor: 'pointer' }} />
        </Tooltip>
    ) : <span />;
    const amountMain = (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <Form.Item name={'currency'} style={{ ...noMargin, width: AMOUNT_CURRENCY_WIDTH, flexShrink: 0 }}>
                    <CurrencySelect style={{ width: '100%' }} disabled={posLocked} />
                </Form.Item>
                <Form.Item name={'amount'} rules={[{ required: true, message: '' }]} style={{ ...noMargin, flex: 1, minWidth: 0 }}>
                    <InputNumber
                        style={{ width: '100%', fontWeight: 600, color: isDiff ? '#cf1322' : undefined }}
                        className="ledger-amount-input"
                        controls={false}
                        formatter={(v) => (isDiff && v !== '' && v != null ? `−${v}` : `${v ?? ''}`)}
                        parser={(v) => (v || '').replace(/[−-]/g, '')}
                        suffix={fillRemainingIcon}
                        disabled={posLocked}
                    />
                </Form.Item>
            </div>
    );
    const amountExtra = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {appointmentCurrency !== currency && (
                <Form.Item name={'currencyAmount'} rules={[{ required: true, message: '' }]} style={noMargin}>
                    <InputNumber style={{ width: '100%' }} addonBefore={`${t('Equivalent')}`} prefix={Currency[appointmentCurrency]} disabled={posLocked} />
                </Form.Item>
            )}
            {/* Tutar kutusunun altında, sağa yaslı. Tahsilat (varsayılan) / Fark ödemesi — onFinish'teki diffPayment mantığı aynen (eksi tutar, açıklamaya "Diff." eklenir) */}
            <Tooltip title={t('Diff payments are saved as a negative amount.')}>
                <div style={{ display: 'inline-flex', alignSelf: 'flex-end' }}>
                    <Form.Item name={'diffPayment'} valuePropName={'checked'} style={noMargin}>
                        <Checkbox disabled={posLocked} style={{ whiteSpace: 'nowrap', color: isDiff ? '#cf1322' : undefined }}>{t('Difference Payment')}</Checkbox>
                    </Form.Item>
                </div>
            </Tooltip>
        </div>
    );
    const amountField = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {amountMain}
            {amountExtra}
        </div>
    );
    // Kaydet: masaüstünde giriş satırının 2. satırında, Tarih'in altından başlar; mobilde tam genişlik
    const saveField = (
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 8, flexDirection: isMobileLayout ? 'column' : 'row' }}>
            <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                block={isMobileLayout}
                size={isMobileLayout ? 'middle' : 'small'}
                style={{ minWidth: 120 }}
            >
                {t('Save')}
            </Button>
            {!isEdit && (
                <Button type="link" size="small" block={isMobileLayout} onClick={resetToNewPayment}>
                    {t('Cancel')}
                </Button>
            )}
        </div>
    );

    // Giriş satırı: yeni ödemede beyaz (geçmiş gri zeminde), düzenlemede amber
    const entryBg = isEdit ? '#fff' : '#fffbe6';
    const renderEntry = () => {
        if (!canCreatePayment) return null;
        if (isMobileLayout) {
            return (
                <div style={{ padding: 12, background: entryBg, display: 'flex', flexDirection: 'column', gap: 8, borderBottom: '1px solid #f0f0f0' }}>
                    {!isEdit && <Text strong style={{ color: '#ad6800' }}><EditOutlined /> {t('Edit Payment')}</Text>}
                    <div>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>{t('Amount')}</div>
                        {amountField}
                    </div>
                    {methodField}
                    {registerField}
                    {dateField}
                    {descriptionField}
                    {saveField}
                </div>
            );
        }
        return (
            <div style={ledgerRow({ background: entryBg, padding: '10px 12px', alignItems: 'start', rowGap: 6, })}>
                {isMultiAppointment && (
                    <Tooltip title={!isEdit ? null : t('Payment is distributed across the selected appointments in order.')}>
                        <Text type="secondary" style={{ ...cell, fontSize: 12, lineHeight: '24px' }}>
                            {!isEdit ? `#${editingAppointmentId ?? ''}` : t('All')}
                        </Text>
                    </Tooltip>
                )}
                {dateField}
                {registerField}
                {methodField}
                {descriptionField}
                {/* Giriş satırında aksiyon hücresi boş: Tutar oraya da yayılır (son iki sütun) */}
                <div style={{ gridColumn: 'span 2' }}>{amountMain}</div>

                {/* 2. satır: Kaydet Tarih–Açıklama altında; karşılık + Fark Ödemesi Tutar altında */}
                <div style={{ gridColumn: isMultiAppointment ? '2 / 6' : '1 / 5' }}>{saveField}</div>
                <div style={{ gridColumn: 'span 2' }}>{amountExtra}</div>
            </div>
        );
    };

    const renderHistory = () => {
        if (payments.length === 0) {
            return <Text type="secondary" style={{ display: 'block', padding: '10px 12px', fontSize: 12 }}>{t('No Payments')}</Text>;
        }
        return payments.map((row, index) => {
            const isEditingRow = !isEdit && row.id === editingId;
            const rowStyle = {
                borderTop: index === 0 ? 'none' : '1px solid #f0f0f0',
                opacity: row.isDeleted ? .55 : 1,
                background: isEditingRow ? '#fffbe6' : undefined,
                fontSize: 12,
                color: '#595959',
            };
            // Dar alanda (mobil / sağ sütun) satır iki katlı: tarih + detay solda, tutar + aksiyonlar sağda
            if (isMobileLayout || sideHistory) {
                return (
                    <div key={row.id} style={{ ...rowStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, padding: '8px 12px' }}>
                        <div style={{ minWidth: 0 }}>
                            <div>
                                {Helper.Date.dateFormat(row.paymentDate)}
                                {row.isDeleted && <Tag style={{ marginLeft: 6 }}>{t('Deleted')}</Tag>}
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {[isMultiAppointment ? `#${row.appointmentId}` : null, row.cashRegister?.name, row.paymentMethod?.name, row.description].filter(Boolean).join(' · ')}
                            </Text>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                            {renderAmount(row)}
                            {renderActionRow(row)}
                        </div>
                    </div>
                );
            }
            return (
                <div key={row.id} style={{ ...ledgerRow(), ...rowStyle }}>
                    {isMultiAppointment && <span style={cell}>#{row.appointmentId}</span>}
                    <span style={{ ...cell, whiteSpace: 'nowrap' }}>{Helper.Date.dateFormat(row.paymentDate)}</span>
                    <span style={ellipsisStyle}>{row.cashRegister?.name}</span>
                    <span style={ellipsisStyle}>{row.paymentMethod?.name}</span>
                    {row.description
                        ? <Tooltip title={row.description}><span style={ellipsisStyle}>{row.description}</span></Tooltip>
                        : <span style={{ ...cell, color: '#bfbfbf' }}>-</span>}
                    {renderAmount(row)}
                    {/* Silinmiş satırda aksiyon yok; etiket o boş hücrede durur, Tarih hücresini taşırıp Kasa'nın üstüne binmez */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {row.isDeleted ? <Tag style={{ margin: 0 }}>{t('Deleted')}</Tag> : renderActionRow(row)}
                    </div>
                </div>
            );
        });
    };

    const ellipsisStyle = { ...cell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };

    // Modal alt sınırın altına daralırsa satırlar kart dışına taşmasın, yatay kaydırılsın.
    // Ödeme ve geçmiş kartları aynı grid şablonunu ve genişliği paylaşır; sütunlar alt alta hizalı kalır.
    const ledgerScroll = (children) => (
        <div style={{ overflowX: isMobileLayout ? undefined : 'auto' }}>
            <div style={{ minWidth: isMobileLayout ? undefined : LEDGER_MIN_WIDTH }}>{children}</div>
        </div>
    );

    // Sütun başlıkları (masaüstü): giriş satırı ve geçmiş için ortak
    const renderLedgerHeader = () => !isMobileLayout && (
        <div style={ledgerRow({ padding: '6px 12px', fontSize: 12, color: '#8c8c8c', borderBottom: '1px solid #f0f0f0' })}>
            {isMultiAppointment && <span style={cell}>{t('App. ID')}</span>}
            <span style={cell}>{t('Date')}</span>
            <span style={cell}>{t('Register')}</span>
            <span style={cell}>{t('Method')}</span>
            <span style={cell}>{t('Description')}</span>
            {/* Tutar başlığı giriş hücresinin iç düzenini izler: Döviz döviz seçicinin, Tutar tutar alanının hizasında */}
            <div style={{ display: 'flex', gap: 4 }}>
                {canCreatePayment && <span style={{ ...cell, width: AMOUNT_CURRENCY_WIDTH, flexShrink: 0 }}>{t('Curr.')}</span>}
                <span style={{ flex: 1, textAlign: 'right', paddingRight: CELL_INSET }}>{t('Amount')}</span>
            </div>
            <span />
        </div>
    );

    return (
        <div ref={wrapperRef} style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%', background: '#f5f7fa', padding: 12 }}>
            <style>{`
                .payment-row-deleted > td { background-color: #fafafa !important; opacity: .55; }
                .payment-row-deleted:hover > td { background-color: #f0f0f0 !important; }
                /* Tutar girişi sağa yaslı: geçmişteki tutarlar ve "Tutar" başlığıyla aynı hizada */
                .ledger-amount-input .ant-input-number-input { text-align: right; }
                /* antd affix'lere pointer-events:none veriyor; kalanı doldur ikonu tıklanabilsin */
                .ledger-amount-input .ant-input-number-suffix { pointer-events: auto; }
            `}</style>

            {/* Geniş ekranda iki sütun: solda İşlemler + Ödemeler, sağda Ödeme Geçmişi (iki satır boyunca) */}
            <div style={sideHistory ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 460px', gap: 12 } : { display: 'contents' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                    {/* 1. İŞLEMLER — alınan hizmetler, fatura kalemi gibi: kompakt tablo */}
                    <Card size="small" style={sectionCard} bodyStyle={{ padding: 0 }}>
                        <Table
                            size="small"
                            dataSource={allItems}
                            rowKey={(row, index) => `${row.type}-${row.id ?? index}`}
                            pagination={false}
                            tableLayout="fixed"
                            // Uzun listede dikey kaydırma; mobilde sütunlar sıkışmasın diye yatay kaydırma
                            // Dar alanda sütunlar birbirini ezmesin (işlem adı görünmez oluyordu): alt sınırın altında yatay kaydırma
                            scroll={{ y: allItems.length > 6 ? 220 : undefined, x: isMultiAppointment ? 560 : 480 }}
                            locale={{ emptyText: <Empty description={t('No Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                            columns={[
                                { key: 'appointmentId', dataIndex: 'appointmentId', title: t('App. ID'), width: 80, render: (v) => `#${v}` },
                                {
                                    key: 'name', title: t('Operation Name'), ellipsis: { showTitle: false }, render: (_, row) => {
                                        const subItems = (() => { try { return JSON.parse(row?.subItems || '[]') } catch { return [] } })();
                                        return (
                                            <>
                                                <Tooltip title={itemName(row)}><span style={{ fontWeight: 500 }}>{itemName(row)}</span></Tooltip>
                                                {subItems.map((item, i) => (
                                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, color: '#8c8c8c' }}>
                                                        <span>• {item?.name}</span>
                                                        <span style={{ whiteSpace: 'nowrap' }}>{formatPrice(item?.price, { currency: row?.currency })}</span>
                                                    </div>
                                                ))}
                                            </>
                                        );
                                    }
                                },
                                { key: 'accessionNumber', title: t('Accession No'), width: 150, render: (_, row) => row.type === 1 && row.accessionNumber ? row.accessionNumber : <Text type="secondary">-</Text> },
                                {
                                    key: 'price', title: t('Price'), align: 'right', width: 130, render: (_, row) => (
                                        <Text strong style={{ whiteSpace: 'nowrap' }}>
                                            {row.price === -1 ? '-' : formatPrice(row.price, { currency: row.currency ?? appointmentCurrency })}
                                        </Text>
                                    )
                                },
                            ]}
                        />
                        {/* ÖZET — toplam / ödenen / kalan borç: kalemlerin altında, aynı kartta */}
                        <PriceSummaryPreview totalPrice={totalPrice} totalPaid={totalPaid} currency={appointmentCurrency}
                            style={{ border: 'none', borderTop: '1px solid #f0f0f0', borderRadius: '0 0 8px 8px' }} />
                    </Card>

                    {/* ÖDEMELER — defter mantığı: yeni ödeme, geçmişle AYNI sütunlarda bir giriş satırı */}
                    {canCreatePayment && (
                        <Card
                            size="small"
                            title={sectionTitle(<WalletOutlined />, t('Payments'))}
                            style={sectionCard}
                            headStyle={sectionHead}
                            bodyStyle={{ padding: 0 }}
                        >
                            {posLocked && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    icon={<CreditCardOutlined />}
                                    style={{ margin: 12, marginBottom: 0 }}
                                    message={t('This payment was sent to the POS, the amount cannot be changed. Cancel it on the POS first.')}
                                />
                            )}
                            <Form
                                size={'small'}
                                form={paymentForm}
                                ref={formRef}
                                onFinish={onFinish}
                                layout={'vertical'}
                                onValuesChange={({ currency, cashRegisterId }) => {
                                    if (currency) setCurrency(currency)
                                    else if (cashRegisterId) {
                                        const reg = registersRef.current.find(x => x.id === cashRegisterId)
                                        formRef.current.setFieldsValue({ paymentMethodId: null })
                                        loadPaymentMethods(reg?.firmId)
                                    }
                                }}
                                initialValues={{ amount: 0, paymentDate: moment(), currency: currency, differencePayment: 0, currencyAmount: 0, diffPayment: false }}
                            >
                                <Form.Item name={'id'} hidden /><Form.Item name={'appointmentId'} hidden />
                                {ledgerScroll(<>{renderLedgerHeader()}{renderEntry()}</>)}
                            </Form>
                        </Card>
                    )}
                </div>

                {/* ÖDEME GEÇMİŞİ — ayrı kart, gri zemin. Sağ sütunda sol sütunun yüksekliğine uyar, fazlası kart içinde kayar.
                    Alt alta düzende ödeme yetkisi yoksa sütun başlıkları burada */}
                <div style={sideHistory ? { position: 'relative', minHeight: 140 } : undefined}>
                    <Card
                        size="small"
                        title={sectionTitle(<HistoryOutlined />, t('Payment History'))}
                        style={{ ...sectionCard, background: '#fafafa', ...(sideHistory ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' } : {}) }}
                        headStyle={{ background: '#fafafa' }}
                        bodyStyle={{ padding: 0, minHeight: 100, ...(sideHistory ? { flex: 1, minHeight: 0, overflowY: 'auto' } : {}) }}
                    >
                        {sideHistory
                            ? renderHistory()
                            : ledgerScroll(<>{!canCreatePayment && renderLedgerHeader()}{renderHistory()}</>)}
                    </Card>
                </div>
            </div>

            {/* 5. KURLAR */}
            {currencyValues.length > 0 && (
                <div style={{ backgroundColor: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Space><GlobalOutlined style={{ color: '#8c8c8c' }} /><Text strong style={{ fontSize: 12 }}>{t('Rates')}:</Text></Space>
                    <Divider type="vertical" />
                    {currencyValues.map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Tag style={{ margin: 0 }}>{c.currency}</Tag><Text style={{ fontSize: 12 }}>{c.value}₺</Text>
                            {i < currencyValues.length - 1 && <Divider type="vertical" />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
